<template>
  <v-form v-model="valid" @submit.prevent="submit">
    <v-card variant="flat" class="contact-form">
      <v-card-text class="pa-4">
        <!-- 1. Type — the lead choice that drives the rest of the form -->
        <div class="mb-2 text-subtitle-2 text-medium-emphasis">
          Wat voeg je toe?
        </div>
        <v-btn-toggle
          v-model="contactData.type"
          mandatory
          divided
          color="primary"
          class="type-toggle mb-2"
          @update:model-value="onContactTypeChange"
        >
          <v-btn
            v-for="t in contactTypes"
            :key="t.type"
            :value="t.type"
            class="type-toggle-btn"
          >
            <v-icon start>{{ getContactIcon(t.type) }}</v-icon>
            {{ t.type_name }}
          </v-btn>
        </v-btn-toggle>
        <div class="text-caption text-medium-emphasis mb-4">
          {{ typeHint }}
        </div>

        <v-alert
          v-if="isEditMode && typeChanged"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-4 text-caption"
        >
          <v-icon start size="small">mdi-information</v-icon>
          Het wijzigen van het contacttype kan gevolgen hebben voor bestaande
          relaties en hiërarchie.
        </v-alert>

        <!-- 2. Parent (only for departments / individuals) -->
        <v-select
          v-if="needsParent"
          v-model="contactData.parent_id"
          :items="relatedContacts"
          :label="parentLabel"
          variant="outlined"
          item-title="name"
          item-value="id"
          density="comfortable"
          :rules="[rules.required]"
          prepend-inner-icon="mdi-office-building-outline"
          class="mb-3"
          clearable
        >
          <template #item="{ props: itemProps, item }">
            <v-list-item
              v-bind="itemProps"
              :prepend-icon="getContactIcon(item.raw.type)"
            >
              <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ getContactTypeLabel(item.raw.type) }}
              </v-list-item-subtitle>
            </v-list-item>
          </template>
        </v-select>

        <!-- 3. Name -->
        <v-text-field
          v-model="contactData.name"
          :label="instantieLabel"
          :placeholder="instantiePlaceholder"
          variant="outlined"
          density="comfortable"
          :rules="[rules.required]"
          :prepend-inner-icon="getContactIcon(contactData.type)"
          class="mb-4"
        />

        <!-- 4. Location(s) — only where a physical address makes sense.
             People inherit their location from their organisation. -->
        <FormSection
          v-if="showLocationSection"
          :title="locationSectionTitle"
          icon="mdi-map-marker"
        >
          <Geocode
            v-model="selectedLocations"
            v-model:primary-place-id="primaryPlaceId"
            :multiple="true"
            :debug="false"
            :label="locationSearchLabel"
            placeholder="Typ om te zoeken"
          />
          <div class="text-caption text-medium-emphasis mt-1">
            {{ locationHint }}
          </div>
        </FormSection>

        <!-- 5. Contact details (phone / email / note) -->
        <FormSection
          :title="detailsSectionTitle"
          icon="mdi-card-account-phone"
        >
          <template #actions>
            <v-menu>
              <template #activator="{ props: menuProps }">
                <v-btn
                  color="primary"
                  variant="tonal"
                  size="small"
                  v-bind="menuProps"
                  prepend-icon="mdi-plus"
                >
                  Toevoegen
                </v-btn>
              </template>
              <v-list density="compact">
                <v-list-item
                  prepend-icon="mdi-phone"
                  @click="addContactDetail('phone_number')"
                >
                  <v-list-item-title>Telefoonnummer</v-list-item-title>
                </v-list-item>
                <v-list-item
                  prepend-icon="mdi-email"
                  @click="addContactDetail('email')"
                >
                  <v-list-item-title>E-mailadres</v-list-item-title>
                </v-list-item>
                <v-list-item
                  prepend-icon="mdi-note-text"
                  @click="addContactDetail('note')"
                >
                  <v-list-item-title>Notitie</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <div v-if="visibleDetails.length === 0" class="text-center py-6">
            <v-icon size="40" color="grey-lighten-1" class="mb-2">
              mdi-card-account-phone-outline
            </v-icon>
            <div class="text-body-2 text-medium-emphasis">
              {{ detailsEmptyHint }}
            </div>
          </div>

          <template
            v-for="(detail, index) in contactDetails"
            :key="`detail-${detail.id || index}`"
          >
            <v-card
              v-if="detail.type !== 'address'"
              variant="outlined"
              class="mb-3"
              :class="{ 'detail-marked-for-deletion': detail._destroy }"
            >
              <v-card-text class="pa-3">
                <ContactDetailEmailForm
                  v-if="detail.type === 'email'"
                  v-model="contactDetails[index]"
                  :show-primary="canSetPrimary('email', index)"
                  :disabled="detail._destroy"
                  @remove="removeContactDetail(index)"
                />
                <ContactDetailPhoneForm
                  v-else-if="detail.type === 'phone_number'"
                  v-model="contactDetails[index]"
                  :show-primary="canSetPrimary('phone_number', index)"
                  :disabled="detail._destroy"
                  @remove="removeContactDetail(index)"
                />
                <ContactDetailNoteForm
                  v-else-if="detail.type === 'note'"
                  v-model="contactDetails[index]"
                  :disabled="detail._destroy"
                  @remove="removeContactDetail(index)"
                />
              </v-card-text>
            </v-card>
          </template>
        </FormSection>

        <!-- 6. Categories -->
        <FormSection title="Categorieën" icon="mdi-tag-multiple">
          <v-select
            v-model="selectedCategoryIds"
            :items="availableCategoryOptions"
            item-title="title"
            item-value="value"
            label="Selecteer categorieën"
            variant="outlined"
            density="comfortable"
            multiple
            chips
            closable-chips
            :loading="contactCategoriesStore.loading"
          >
            <template #chip="{ props: chipProps, item }">
              <v-chip
                v-bind="chipProps"
                :color="item.raw.color"
                variant="tonal"
                closable
              >
                <template #prepend>
                  <v-icon size="small" class="mr-1">{{ item.raw.icon }}</v-icon>
                </template>
                {{ item.raw.title }}
              </v-chip>
            </template>
            <template #item="{ props: itemProps, item }">
              <v-list-item
                v-bind="itemProps"
                :disabled="
                  !item.raw.active &&
                  !selectedCategoryIds.includes(item.raw.value)
                "
              >
                <template #prepend>
                  <v-checkbox-btn
                    :model-value="selectedCategoryIds.includes(item.raw.value)"
                    :disabled="
                      !item.raw.active &&
                      !selectedCategoryIds.includes(item.raw.value)
                    "
                  />
                  <v-icon :color="item.raw.color" class="ml-2">
                    {{ item.raw.icon }}
                  </v-icon>
                </template>
                <v-list-item-title>
                  {{ item.raw.title }}
                  <v-chip
                    v-if="!item.raw.active"
                    size="x-small"
                    color="warning"
                    variant="outlined"
                    class="ml-2"
                  >
                    Inactief
                  </v-chip>
                </v-list-item-title>
              </v-list-item>
            </template>
          </v-select>
          <div
            v-if="hasInactiveSelectedCategories"
            class="text-caption text-warning mt-1"
          >
            <v-icon size="small" color="warning">mdi-information</v-icon>
            Inactieve categorieën worden verwijderd wanneer je ze deselecteert.
          </div>
        </FormSection>

        <!-- Error Message -->
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          class="mt-3"
          :text="error"
        />
      </v-card-text>

      <v-card-actions class="pa-4 pt-0">
        <v-btn v-if="!popup" variant="outlined" @click="cancel">
          Annuleren
        </v-btn>
        <v-spacer />
        <v-btn
          type="submit"
          color="primary"
          variant="elevated"
          :loading="loading"
          :disabled="!valid"
        >
          {{ isEditMode ? "Bijwerken" : "Opslaan" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useContactsStoreRefactor } from "@/stores/contactsStoreRefactor";
import { useContactCategoriesStoreRefactor } from "@/stores/contactCategoriesStoreRefactor";
import { useFormsStore } from "@/stores/formsStore";
import { useAlertStore } from "@/stores/alertStore";
import ContactDetailEmailForm from "./ContactDetailEmailForm.vue";
import ContactDetailPhoneForm from "./ContactDetailPhoneForm.vue";
import ContactDetailNoteForm from "./ContactDetailNoteForm.vue";
import FormSection from "./FormSection.vue";
import Geocode from "@/components/geocode/Geocode.vue";

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
  popup: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["saved", "created", "updated"]);

// Store instances
const router = useRouter();
const contactsStore = useContactsStoreRefactor();
const contactCategoriesStore = useContactCategoriesStoreRefactor();
const formsStore = useFormsStore();
const alertStore = useAlertStore();

// Reactive data
const valid = ref(false);
const loading = ref(false);
const error = ref(null);

// Contact data - simple local reactive copy (like ReportForm)
const contactData = ref({ ...props.contact });
const originalType = ref(props.contact?.type || null);

// Contact details - simple local reactive array
const contactDetails = ref([]);

// Locations state
const selectedLocations = ref([]); // array of { place_id, formatted_address, ... }
const primaryPlaceId = ref(null);

// Categories state
const selectedCategoryIds = ref([]);

// Watch props and sync local state (like ReportForm and DayReportForm)
watch(
  () => props.contact,
  (newContact) => {
    Object.assign(contactData.value, newContact);
  },
  { immediate: true, deep: true },
);

// Form options
const contactTypes = [
  { type_name: "Bedrijf", type: "organization" },
  { type_name: "Afdeling", type: "department" },
  { type_name: "Persoon", type: "individual" },
];

const rules = {
  required: (value) => !!value || "Dit veld is verplicht",
};

// Computed properties
const isEditMode = computed(() => !!contactData.value.id);

const typeChanged = computed(
  () => originalType.value && contactData.value.type !== originalType.value,
);

const needsParent = computed(() =>
  ["department", "individual"].includes(contactData.value.type),
);

// People inherit their location from their organisation, so we only surface a
// physical-address search for organisations and departments (branches).
const showLocationSection = computed(
  () => contactData.value.type !== "individual",
);

const typeHint = computed(() => {
  const hints = {
    organization: "Een organisatie of instantie, bijv. GGD of een gemeente.",
    department:
      "Een vestiging of afdeling van een organisatie, bijv. GGD — Haarlem.",
    individual: "Een persoon die bij een organisatie of afdeling werkt.",
  };
  return hints[contactData.value.type] || "";
});

const parentLabel = computed(() => {
  if (contactData.value.type === "department") return "Onderdeel van (bedrijf)";
  if (contactData.value.type === "individual") {
    return "Werkt bij (bedrijf of afdeling)";
  }
  return "Gerelateerd contact";
});

const instantieLabel = computed(() => {
  const labels = {
    organization: "Bedrijfsnaam",
    department: "Afdelings- / vestigingsnaam",
    individual: "Volledige naam",
  };
  return labels[contactData.value.type] || "Naam";
});

const instantiePlaceholder = computed(() => {
  const placeholders = {
    organization: "Bijv. GGD Kennemerland",
    department: "Bijv. Haarlem",
    individual: "Bijv. Jan van der Berg",
  };
  return placeholders[contactData.value.type] || "Voer de naam in";
});

const locationSectionTitle = computed(() =>
  contactData.value.type === "department" ? "Vestigingsadres" : "Adres(sen)",
);

const locationSearchLabel = computed(() =>
  contactData.value.type === "department"
    ? "Zoek het vestigingsadres"
    : "Zoek adres, regio of land",
);

const locationHint = computed(() =>
  contactData.value.type === "department"
    ? "Het fysieke adres van deze vestiging."
    : "Eén of meer adressen. Markeer er één als hoofdadres.",
);

const detailsSectionTitle = computed(() =>
  contactData.value.type === "individual"
    ? "Contactgegevens"
    : "Contactgegevens (optioneel)",
);

const detailsEmptyHint = computed(() =>
  contactData.value.type === "individual"
    ? "Voeg een telefoonnummer of e-mailadres toe."
    : "Nog geen contactgegevens. Voeg er eventueel toe.",
);

// Address details are legacy; new addresses go through the location search.
const visibleDetails = computed(() =>
  contactDetails.value.filter((d) => d.type !== "address" && !d._destroy),
);

const relatedContacts = computed(() => {
  if (!contactsStore.entities) return [];

  let validTypes = ["department", "organization"];
  if (contactData.value.type === "department") {
    validTypes = ["organization"];
  }

  return contactsStore.entities.filter(
    (contact) =>
      validTypes.includes(contact.type) && contact.id !== contactData.value.id,
  );
});

// Category options - for editing, show active + currently selected inactive
const availableCategoryOptions = computed(() => {
  if (isEditMode.value) {
    return contactCategoriesStore.getEditOptions(selectedCategoryIds.value);
  }
  return contactCategoriesStore.activeCategoryOptions;
});

const hasInactiveSelectedCategories = computed(() => {
  if (!isEditMode.value) return false;
  const allCategories = contactCategoriesStore.categories;
  return selectedCategoryIds.value.some((id) => {
    const cat = allCategories.find((c) => c.id === id);
    return cat && !cat.active;
  });
});

// Helper methods
const getContactIcon = (type) => {
  return contactsStore.icons[type]?.icon || "mdi-help-circle";
};

const getContactTypeLabel = (type) => {
  const labels = {
    organization: "Bedrijf",
    department: "Afdeling",
    individual: "Persoon",
  };
  return labels[type] || "Onbekend";
};

const onContactTypeChange = () => {
  // Reset parent when type changes to avoid validation conflicts
  contactData.value.parent_id = null;

  if (isEditMode.value) {
    alertStore.showSuccess(
      "Type aangepast - De parent relatie is gereset. Selecteer indien nodig een nieuwe parent.",
      { timeout: 5000 },
    );
  }
};

const addContactDetail = (type) => {
  const newDetail = {
    type,
    primary: false,
  };

  switch (type) {
    case "email":
      newDetail.email = "";
      break;
    case "phone_number":
      newDetail.phone_number = "";
      newDetail.phone_type = "mobile";
      newDetail.meta = "";
      break;
    case "note":
      newDetail.note = "";
      newDetail.note_type = "general";
      newDetail.note_date = new Date().toISOString().split("T")[0];
      break;
  }

  contactDetails.value.push(newDetail);
};

const removeContactDetail = (index) => {
  const detail = contactDetails.value[index];

  if (detail) {
    if (!detail.id) {
      contactDetails.value.splice(index, 1);
    } else {
      detail._destroy = !detail._destroy;
    }
  }
};

const canSetPrimary = (type, currentIndex) => {
  const sameTypeDetails = contactDetails.value.filter(
    (detail, index) => detail.type === type && index !== currentIndex,
  );
  return sameTypeDetails.length > 0;
};

const loadContacts = async () => {
  try {
    await contactsStore.getAll();
  } catch (err) {
    console.error("Error loading contacts:", err);
  }
};

// Lifecycle
onMounted(async () => {
  await Promise.all([loadContacts(), contactCategoriesStore.fetchAll()]);

  // Set defaults like ReportForm does
  if (!contactData.value.id) contactData.value.id = null;
  if (!contactData.value.name) contactData.value.name = "";
  if (!contactData.value.type) contactData.value.type = "organization";
  if (!contactData.value.parent_id) contactData.value.parent_id = null;
  originalType.value = props.contact?.type || null;

  // Load contact details if this is an existing contact
  if (
    props.contact &&
    props.contact.id &&
    (props.contact.emails ||
      props.contact.phone_numbers ||
      props.contact.addresses ||
      props.contact.notes)
  ) {
    const mapSTIClassToType = (stiClass) => {
      const classMap = {
        "Contact::Detail::Email": "email",
        "Contact::Detail::PhoneNumber": "phone_number",
        "Contact::Detail::Address": "address",
        "Contact::Detail::Note": "note",
      };
      return classMap[stiClass] || stiClass;
    };

    contactDetails.value = [];

    if (props.contact.emails) {
      props.contact.emails.forEach((email) => {
        contactDetails.value.push({
          ...email,
          type: mapSTIClassToType(email.type) || "email",
        });
      });
    }
    if (props.contact.phone_numbers) {
      props.contact.phone_numbers.forEach((phone) => {
        contactDetails.value.push({
          ...phone,
          type: mapSTIClassToType(phone.type) || "phone_number",
        });
      });
    }
    if (props.contact.addresses) {
      props.contact.addresses.forEach((address) => {
        contactDetails.value.push({
          ...address,
          type: mapSTIClassToType(address.type) || "address",
        });
      });
    }
    if (props.contact.notes) {
      props.contact.notes.forEach((note) => {
        contactDetails.value.push({
          ...note,
          type: mapSTIClassToType(note.type) || "note",
        });
      });
    }
  }

  // Load linked_locations if editing
  if (
    props.contact &&
    props.contact.id &&
    Array.isArray(props.contact.linked_locations)
  ) {
    const sorted = [...props.contact.linked_locations].sort(
      (a, b) => (b.primary === true) - (a.primary === true),
    );
    selectedLocations.value = sorted
      .map((ll) => ({
        place_id: ll.location?.place_id,
        formatted_address: ll.location?.formatted_address,
        name: ll.location?.name,
        lat: ll.location?.latitude,
        lng: ll.location?.longitude,
      }))
      .filter((x) => x.place_id);
    const primary = sorted.find((ll) => ll.primary);
    primaryPlaceId.value = primary?.location?.place_id || null;
  }

  // Load contact categories if editing
  if (
    props.contact &&
    props.contact.id &&
    Array.isArray(props.contact.contact_category_ids)
  ) {
    selectedCategoryIds.value = [...props.contact.contact_category_ids];
  }
});

const submit = async () => {
  if (!valid.value) return;

  try {
    loading.value = true;
    error.value = null;

    const mapTypeToSTIClass = (type) => {
      const typeMap = {
        email: "Contact::Detail::Email",
        phone_number: "Contact::Detail::PhoneNumber",
        address: "Contact::Detail::Address",
        note: "Contact::Detail::Note",
      };
      return typeMap[type] || type;
    };

    const submitData = {
      ...contactData.value,
      contact_details_attributes: contactDetails.value.map((detail) => {
        const { type, primary, _destroy, ...detailData } = detail;
        const mappedDetail = {
          type: mapTypeToSTIClass(type),
          primary: primary || false,
          ...detailData,
        };
        if (_destroy) {
          mappedDetail._destroy = _destroy;
        }
        return mappedDetail;
      }),
      // Locations (only meaningful for org/department, but harmless otherwise)
      place_ids: selectedLocations.value.map((loc) => loc.place_id),
      primary_place_id: primaryPlaceId.value,
      contact_category_ids: selectedCategoryIds.value,
    };

    let result;
    if (isEditMode.value) {
      result = await contactsStore.update(submitData);
      alertStore.success("Contact succesvol bijgewerkt");
      emit("updated", result);
    } else {
      result = await contactsStore.create(submitData);
      alertStore.success("Contact succesvol aangemaakt");
      emit("created", result);
    }

    emit("saved", result);

    if (props.popup) {
      formsStore.$reset();
    } else {
      router.push(`/contacts/${result.id}`);
    }
  } catch (err) {
    console.error("Error saving contact:", err);
    error.value = err.message || "Er is een fout opgetreden bij het opslaan";
  } finally {
    loading.value = false;
  }
};

const cancel = () => {
  if (props.popup) {
    formsStore.$reset();
  } else {
    router.go(-1);
  }
};
</script>

<style scoped>
.contact-form {
  max-width: 100%;
}

.type-toggle {
  width: 100%;
  height: auto;
}

.type-toggle-btn {
  flex: 1 1 0;
  min-height: 48px;
}

.detail-marked-for-deletion {
  opacity: 0.5;
  background-color: rgba(var(--v-theme-error), 0.05) !important;
  border-color: rgba(var(--v-theme-error), 0.3) !important;
}

@media (max-width: 600px) {
  .type-toggle {
    flex-direction: column;
  }

  .type-toggle-btn {
    width: 100%;
  }
}
</style>
